import React, { createContext, useContext, useCallback } from 'react';

import { track } from '@adsk/acs-web-analytics';
import { TOOL_IDS, useCurrentAccountId, useCurrentProductId, useCurrentToolId } from '@adsk/alloy-provider';

const getProductInfo = () => {
  const results = /\/([^\/?&#]+)\/([^\/?&#]+)\/projects\//.exec(window.location.pathname);
  if (results !== null && results.length >= 3) return { productId: results[1], toolId: results[2] };
  return {};
};

const getToolId = (toolId?: string) => {
  // Use TOOL_IDS.DOCUMENTS instead of TOOL_IDS.FILES for analytics
  return toolId === TOOL_IDS.FILES ? TOOL_IDS.DOCUMENTS : toolId;
};

interface AnalyticsContextProps {
  trackEvent: (eventName: string, properties?: Record<string, unknown>) => void;
}
interface AnalyticsProviderProps {
  children: React.ReactNode;
}

const AnalyticsContext = createContext<AnalyticsContextProps>({
  trackEvent: () => {}
});

const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const accountId = useCurrentAccountId();
  const productId = useCurrentProductId();
  const toolId = useCurrentToolId();

  /**
   * Add extra properties when tracing events which is suggested by provider of acs-web-analytics
   * Check https://autodesk.slack.com/archives/C07FEHPU72N/p1722929718817009?thread_ts=1722779272.245559&cid=C07FEHPU72N
   */
  const trackEvent = useCallback(
    (eventName: string, properties?: Record<string, unknown>) => {
      const extraProps = {
        account_id: '',
        product_id: '',
        tool_id: ''
      };

      const {
        productId: pId,
        toolId: tId
      }: {
        productId?: string;
        toolId?: string;
      } = getProductInfo();
      const currentProductId = productId || pId;
      const currentToolId = getToolId(toolId || tId);

      if (accountId) extraProps.account_id = accountId;
      if (currentProductId) extraProps.product_id = currentProductId;
      if (currentToolId) extraProps.tool_id = currentToolId;

      return track(eventName, { ...extraProps, ...properties });
    },
    [accountId, productId, toolId]
  );

  return <AnalyticsContext.Provider value={{ trackEvent }}>{children}</AnalyticsContext.Provider>;
};

const useAnalytics = () => useContext(AnalyticsContext);

export { AnalyticsProvider, useAnalytics };
